import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, Plus, Pencil, Baby, Warning, HouseSimple, UserMinus, ArrowClockwise, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  computeAge,
  createChild,
  getChild,
  listChildren,
  reinstateChild,
  updateChild,
  withdrawChild,
  SCHEDULE_PATTERNS,
  SCHEDULE_SHORT,
  multiplierForPattern,
} from "@/lib/admin/children";
import { listClassroomsForSelect } from "@/lib/admin/staff";
import ChildAvatar from "@/components/children/ChildAvatar";
import ChildProfilePhotoEditor from "@/components/children/ChildProfilePhotoEditor";
import AdminPickupListPanel from "@/components/pickup/AdminPickupListPanel";
import AdminMedicationsPanel from "@/components/medications/AdminMedicationsPanel";

export default function AdminChildren() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [classroomFilter, setClassroomFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [drawer, setDrawer] = useState(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, rooms, fams] = await Promise.all([
        listChildren({ search: searchRef.current, classroomId: classroomFilter || null, status: statusFilter || null }),
        listClassroomsForSelect(),
        supabase.from("families").select("id,display_name").order("display_name"),
      ]);
      setRows(list);
      setClassrooms(rooms);
      setFamilies(fams.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load children");
    } finally {
      setLoading(false);
    }
  }, [classroomFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((c) =>
      `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(s) ||
      (c.family?.display_name || "").toLowerCase().includes(s),
    );
  }, [rows, search]);

  const kpis = {
    total: rows.length,
    active: rows.filter((r) => !r.withdrawn_at).length,
    allergies: rows.filter((r) => !r.withdrawn_at && (r.allergies || "").trim().length > 0).length,
    unassigned: rows.filter((r) => !r.withdrawn_at && !r.classroom_id).length,
  };

  const onWithdraw = async (child) => {
    const date = window.prompt("Withdrawal date (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
    if (!date) return;
    try {
      await withdrawChild(child.id, date);
      toast.success(`${child.first_name} withdrawn`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not withdraw");
    }
  };

  const onReinstate = async (child) => {
    try {
      await reinstateChild(child.id);
      toast.success(`${child.first_name} reinstated`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not reinstate");
    }
  };

  return (
    <div data-testid="admin-children" className="space-y-6">
      <section>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Operational
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Children
            </h1>
            <p className="mt-2 max-w-2xl text-neutral-600">
              Manage enrolled children, their classroom, and medical details.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-children-new"
            onClick={() => setDrawer({ mode: "create" })}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
          >
            <Plus size={14} weight="bold" />
            Add child
          </button>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testId="admin-children-kpi-total" label="Children" value={String(kpis.total)} />
        <Kpi testId="admin-children-kpi-active" label="Active" value={String(kpis.active)} accent />
        <Kpi testId="admin-children-kpi-allergies" label="With allergies" value={String(kpis.allergies)} />
        <Kpi testId="admin-children-kpi-unassigned" label="No classroom" value={String(kpis.unassigned)} />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-2">
          <MagnifyingGlass size={18} weight="duotone" className="text-primary-hover" />
          <input
            data-testid="admin-children-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by child or family name"
            className="flex-1 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="admin-children-filter-classroom"
            value={classroomFilter}
            onChange={(e) => setClassroomFilter(e.target.value)}
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">All classrooms</option>
            {classrooms.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          {["active", "withdrawn", ""].map((s) => (
            <button
              key={s || "all"}
              type="button"
              data-testid={`admin-children-filter-${s || "all"}`}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                statusFilter === s
                  ? "border-primary/40 bg-primary text-white"
                  : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              {s === "active" ? "Active" : s === "withdrawn" ? "Withdrawn" : "All"}
            </button>
          ))}
        </div>
      </section>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyCard testId="admin-children-empty" title="No children match" desc="Try adjusting your filters or adding a new child." />
      ) : (
        <div data-testid="admin-children-table" className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
                <th className="px-4 py-3">Child</th>
                <th className="px-4 py-3">Family</th>
                <th className="px-4 py-3">Classroom</th>
                <th className="px-4 py-3">Schedule</th>
                <th className="px-4 py-3">Age</th>
                <th className="px-4 py-3">Alerts</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const age = computeAge(c.date_of_birth);
                return (
                  <tr key={c.id} data-testid={`admin-children-row-${c.id}`} className="border-t border-neutral-100">
                    <td className="px-4 py-3 font-semibold text-neutral-900">
                      <div className="flex items-center gap-2">
                        <ChildAvatar
                          path={c.profile_photo_path}
                          name={`${c.first_name || ""} ${c.last_name || ""}`}
                          size={32}
                          testId={`admin-children-row-${c.id}-avatar`}
                        />
                        {c.first_name} {c.last_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{c.family?.display_name || "—"}</td>
                    <td className="px-4 py-3 text-neutral-700">
                      {c.classroom?.name || <span className="text-neutral-400">Unassigned</span>}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const pattern = c.schedule_pattern || "full_time";
                        const mult = Number(c.tuition_multiplier ?? 1);
                        const isPart = pattern !== "full_time" || mult < 1;
                        const label = SCHEDULE_SHORT[pattern] || "Full-time";
                        return (
                          <span
                            data-testid={`admin-children-row-${c.id}-schedule`}
                            data-pattern={pattern}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              isPart
                                ? "border-amber-300 bg-amber-50 text-amber-800"
                                : "border-primary/30 bg-primary/10 text-primary-hover"
                            }`}
                          >
                            <Calendar size={10} weight="duotone" />
                            {label}
                            {isPart && mult < 1 && (
                              <span className="ml-0.5 font-mono">{Math.round(mult * 100)}%</span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{age?.label || "—"}</td>
                    <td className="px-4 py-3">
                      {(c.allergies || "").trim() && (
                        <span
                          data-testid={`admin-children-row-${c.id}-allergy`}
                          className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-[#C6851E]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#C6851E]"
                        >
                          <Warning size={10} weight="duotone" />
                          Allergy
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.withdrawn_at ? (
                        <span className="inline-flex rounded-full border border-neutral-200 bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                          Withdrawn
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          data-testid={`admin-children-row-${c.id}-edit`}
                          onClick={() => setDrawer({ mode: "edit", childId: c.id })}
                          className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/10"
                        >
                          <Pencil size={11} weight="bold" />
                          Edit
                        </button>
                        {c.withdrawn_at ? (
                          <button
                            type="button"
                            data-testid={`admin-children-row-${c.id}-reinstate`}
                            onClick={() => onReinstate(c)}
                            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
                          >
                            <ArrowClockwise size={11} weight="bold" />
                            Reinstate
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid={`admin-children-row-${c.id}-withdraw`}
                            onClick={() => onWithdraw(c)}
                            className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
                          >
                            <UserMinus size={11} weight="bold" />
                            Withdraw
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <ChildSheet
          mode={drawer.mode}
          childId={drawer.childId}
          organizationId={profile?.organization_id}
          classrooms={classrooms}
          families={families}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
        />
      )}
    </div>
  );
}

function ChildSheet({ mode, childId, organizationId, classrooms, families, onClose, onSaved }) {
  const { profile } = useAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    family_id: "",
    classroom_id: "",
    date_of_birth: "",
    enrolled_at: "",
    allergies: "",
    medical_notes: "",
    schedule_pattern: "full_time",
    tuition_multiplier: 1.000,
  });
  const [saving, setSaving] = useState(false);
  const [loadedChild, setLoadedChild] = useState(null);

  useEffect(() => {
    if (mode !== "edit" || !childId) return;
    (async () => {
      try {
        const c = await getChild(childId);
        if (!c) return;
        setLoadedChild(c);
        setForm({
          first_name: c.first_name || "",
          last_name: c.last_name || "",
          family_id: c.family_id || "",
          classroom_id: c.classroom_id || "",
          date_of_birth: c.date_of_birth || "",
          enrolled_at: c.enrolled_at || "",
          allergies: c.allergies || "",
          medical_notes: c.medical_notes || "",
          schedule_pattern: c.schedule_pattern || "full_time",
          tuition_multiplier: c.tuition_multiplier ?? 1.000,
        });
      } catch (err) {
        toast.error(err.message || "Could not load child");
      }
    })();
  }, [mode, childId]);

  const onSave = async () => {
    if (!form.first_name.trim()) return toast.error("First name required");
    if (mode === "create" && !form.family_id) return toast.error("Pick a family");
    const mult = Number(form.tuition_multiplier);
    if (!Number.isFinite(mult) || mult < 0 || mult > 1) {
      return toast.error("Tuition multiplier must be between 0 and 1");
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        family_id: form.family_id,
        classroom_id: form.classroom_id || null,
        date_of_birth: form.date_of_birth || null,
        enrolled_at: form.enrolled_at || null,
        allergies: form.allergies.trim() || null,
        medical_notes: form.medical_notes.trim() || null,
        schedule_pattern: form.schedule_pattern,
        tuition_multiplier: Number(mult.toFixed(3)),
      };
      if (mode === "create") {
        await createChild({ organization_id: organizationId, ...payload });
        toast.success("Child added");
      } else {
        await updateChild(childId, payload);
        toast.success("Child updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onPickPattern = (pattern) => {
    const presetMult = multiplierForPattern(pattern);
    setForm((prev) => ({
      ...prev,
      schedule_pattern: pattern,
      // Preset patterns auto-fill multiplier; "custom" leaves the existing
      // value alone so the admin can keep typing.
      tuition_multiplier:
        presetMult != null ? presetMult : prev.tuition_multiplier,
    }));
  };

  const input = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="admin-child-sheet">
        <SheetHeader>
          <SheetTitle>{mode === "create" ? "Add child" : `${form.first_name} ${form.last_name}`.trim() || "Child"}</SheetTitle>
          <SheetDescription>
            {mode === "create"
              ? "Link a child to a family, assign a classroom, and capture any medical info."
              : "Update classroom, dates, or medical details."}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          {mode === "edit" && loadedChild?.id && (
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3">
              <ChildProfilePhotoEditor
                child={loadedChild}
                onChanged={(newPath) =>
                  setLoadedChild((prev) => (prev ? { ...prev, profile_photo_path: newPath } : prev))
                }
                testId={`admin-child-photo-${loadedChild.id}`}
              />
            </div>
          )}
          {mode === "edit" && loadedChild?.id && (
            <AdminPickupListPanel childId={loadedChild.id} />
          )}
          {mode === "edit" && loadedChild?.id && (
            <AdminMedicationsPanel
              childId={loadedChild.id}
              profileId={profile?.id}
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">First name</span>
              <input data-testid="admin-child-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={input} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Last name</span>
              <input data-testid="admin-child-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={input} />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              <HouseSimple size={12} weight="duotone" /> Family
            </span>
            <select
              data-testid="admin-child-family"
              value={form.family_id}
              onChange={(e) => setForm({ ...form, family_id: e.target.value })}
              disabled={mode === "edit"}
              className={`${input} ${mode === "edit" ? "opacity-60" : ""}`}
            >
              <option value="">— Select family —</option>
              {families.map((f) => (
                <option key={f.id} value={f.id}>{f.display_name}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Classroom</span>
            <select data-testid="admin-child-classroom" value={form.classroom_id} onChange={(e) => setForm({ ...form, classroom_id: e.target.value })} className={input}>
              <option value="">— Unassigned —</option>
              {classrooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </label>

          <div className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-3 space-y-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                Schedule & tuition multiplier
              </span>
              <span
                data-testid="admin-child-multiplier-display"
                className="font-mono text-xs font-semibold text-neutral-900"
              >
                {Math.round(Number(form.tuition_multiplier || 0) * 100)}% of full
              </span>
            </div>
            <div data-testid="admin-child-schedule-chips" className="flex flex-wrap gap-1.5">
              {SCHEDULE_PATTERNS.map((p) => {
                const active = form.schedule_pattern === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    data-testid={`admin-child-schedule-${p.value}`}
                    data-active={active}
                    onClick={() => onPickPattern(p.value)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      active
                        ? "border-primary bg-primary text-white"
                        : "border-neutral-200 bg-white text-neutral-700 hover:border-primary/40"
                    }`}
                  >
                    {p.short}
                  </button>
                );
              })}
            </div>
            {form.schedule_pattern === "custom" && (
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                  Custom multiplier (0 – 1)
                </span>
                <input
                  data-testid="admin-child-multiplier-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={form.tuition_multiplier}
                  onChange={(e) => setForm({ ...form, tuition_multiplier: e.target.value })}
                  className={input}
                />
              </label>
            )}
            <p className="text-[11px] leading-relaxed text-neutral-500">
              Full-time bills 100%. Part-time presets auto-fill 80% (4-day),
              60% (3-day), 40% (2-day). Pick "Custom" for any other rate.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Date of birth</span>
              <input data-testid="admin-child-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={input} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Enrolled on</span>
              <input data-testid="admin-child-enrolled" type="date" value={form.enrolled_at} onChange={(e) => setForm({ ...form, enrolled_at: e.target.value })} className={input} />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Allergies</span>
            <input data-testid="admin-child-allergies" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} placeholder="Peanuts, shellfish…" className={input} />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Medical notes</span>
            <textarea data-testid="admin-child-medical" rows={3} value={form.medical_notes} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} placeholder="EpiPen in backpack, uses inhaler…" className={input} />
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="admin-child-save"
              disabled={saving}
              onClick={onSave}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              {saving ? "Saving…" : mode === "create" ? "Add child" : "Save changes"}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-5 ${accent ? "border-primary/30" : "border-neutral-200"}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${accent ? "text-primary-hover" : "text-neutral-900"}`}>{value}</p>
    </div>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-16"><div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" /></div>;
}

function EmptyCard({ title, desc, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
      <p className="font-heading text-base font-semibold text-neutral-900">{title}</p>
      <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">{desc}</p>
    </div>
  );
}
